import { FormPagePropertyItemSingleSelectionDrillDownSetting } from 'backend';
import { FieldProps } from 'formik';
import {
  AssortedDrillDownPropertiesGroup,
  convertValuesForDrillDownFromAssortedPropertiesGroup,
} from 'models';
import { useCallback } from 'react';
import {
  FormPageInputData,
  FormPageProperty,
  FormPageInputItem,
  FormPageInputItemSingleSelectionPulldown,
  INPUT_ITEM_TYPE,
} from 'shared';

type InputItem = FormPageInputItemSingleSelectionPulldown;

const getParentValue = (
  formPageInputData: FormPageInputData,
  drillDownSetting?: FormPagePropertyItemSingleSelectionDrillDownSetting,
): InputItem['value'] => {
  const parentId = drillDownSetting?.parentId;
  const parentInputItem =
    (parentId && formPageInputData[parentId]) || undefined;
  if (
    parentInputItem?.inputItemType !== INPUT_ITEM_TYPE.singleSelectionPulldown
  ) {
    return undefined;
  }
  return parentInputItem.value;
};

type useDrillDownFieldPropsHandlerPayload = {
  fieldProps: FieldProps<FormPageInputItem, FormPageInputData>;
  assortedDrillDownSettingPropertiesGroup?: AssortedDrillDownPropertiesGroup;
  propertyId: FormPageProperty['propertyId'];
  drillDownSetting?: FormPagePropertyItemSingleSelectionDrillDownSetting;
};

type UpdateDrillDownFieldValue = (
  key: keyof Pick<InputItem, 'value'>,
  value: InputItem['value'],
) => void;

type useDrillDownFieldPropsHandlerReturnType = {
  updateDrillDownFieldValue: UpdateDrillDownFieldValue;
  parentValue: InputItem['value'];
};

export const useDrillDownFieldPropsHandler = ({
  fieldProps,
  propertyId,
  assortedDrillDownSettingPropertiesGroup,
  drillDownSetting,
}: useDrillDownFieldPropsHandlerPayload): useDrillDownFieldPropsHandlerReturnType => {
  const { setValues, values } = fieldProps.form;
  const updateDrillDownFieldValue: UpdateDrillDownFieldValue = useCallback(
    (key, value) => {
      setValues((values) => {
        const newValues = {
          ...values,
          [propertyId]: {
            ...values[propertyId],
            [key]: value,
          },
        };
        if (!assortedDrillDownSettingPropertiesGroup?.length) {
          return newValues;
        }
        return convertValuesForDrillDownFromAssortedPropertiesGroup(
          assortedDrillDownSettingPropertiesGroup,
          newValues,
        );
      });
    },
    [setValues, propertyId, assortedDrillDownSettingPropertiesGroup],
  );
  const parentValue = getParentValue(values, drillDownSetting);

  return {
    updateDrillDownFieldValue,
    parentValue,
  };
};
